/**
 * Le utenze, viste dalla pagina.
 *
 * **La parola d'ordine torna una volta sola.** Non c'e' modo di rileggerla — in
 * archivio ne resta l'impronta — e per questo il tipo la porta accanto
 * all'utenza appena aperta e in nessun altro punto: se comparisse anche
 * nell'elenco, il primo che lo rinfresca la vedrebbe di nuovo, e la promessa
 * «la conosce solo chi la consegna» sarebbe finita.
 *
 * **L'ultimo manager lo protegge il servizio, non la schermata.** Qui non c'e'
 * nessun conteggio dei manager rimasti: contarlo nella pagina darebbe una
 * risposta vecchia di quanto e' vecchio l'elenco che si sta guardando, e due
 * schede aperte insieme si direbbero l'una all'altra che l'altro c'e' ancora.
 * La pagina chiede, e legge il rifiuto `last_manager`.
 */
import { get, newKey, patch, send } from '../api'
import type { MessageKey } from '../i18n/messages'

/** Il nome del ruolo come lo legge una persona.
 *
 * Sta qui e non nella schermata delle utenze perche' non lo dice piu' solo
 * lei: lo dice anche il riquadro d'avvio, che saluta chi entra chiamandolo
 * col proprio mestiere. Due tabelle si separerebbero, e la seconda a
 * divergere sarebbe quella che nessuno guarda.
 */
const ROLE_KEY: Record<string, MessageKey> = {
  uploader: 'role.uploader',
  qa_operator: 'role.qa_operator',
  supervisor: 'role.supervisor',
  job_manager: 'role.job_manager',
}

export function namedRole(t: (key: MessageKey) => string, value: string): string {
  const key = ROLE_KEY[value]
  // Un ruolo che non sta nella tabella si mostra com'e': meglio un codice che
  // un nome inventato.
  return key !== undefined ? t(key) : value
}

export interface Account {
  id: string
  login: string
  display_name: string
  role: string
  active: boolean
  must_change_password: boolean
}

/** L'utenza appena aperta e la parola d'ordine da consegnare a voce. */
export interface Opened {
  account: Account
  password: string
}

export interface NewAccount {
  login: string
  display_name: string
  role: string
}

/** I ruoli che si possono assegnare, coi nomi con cui si leggono in italiano.
 *
 * L'elenco e' quello della matrice dei permessi del servizio. Sta qui perche'
 * un menu' non puo' che offrire un insieme finito, ma la convalida vera resta
 * al servizio: un ruolo fuori dalla matrice viene rifiutato per nome, e non
 * silenziosamente accettato. */
export const ROLES: { value: string; label: string }[] = [
  { value: 'uploader', label: 'Chi carica i pacchetti' },
  { value: 'qa_operator', label: 'Operatore di verifica' },
  { value: 'supervisor', label: 'Supervisore' },
  { value: 'job_manager', label: 'Responsabile della commessa' },
]

/** Un ruolo e le azioni che il servizio gli permette. */
export interface Role {
  role: string
  may: string[]
}

/** La matrice dei permessi, dal servizio: e' la tabella che rifiuta davvero
 *  le richieste, non una copia tenuta qui. */
export function readRoles(): Promise<Role[]> {
  return get<Role[]>('/users/roles')
}

export function readAccounts(): Promise<Account[]> {
  return get<Account[]>('/users')
}

export function openAccount(account: NewAccount, key = newKey()): Promise<Opened> {
  return send<Opened>('/users', account, key)
}

export function resetPassword(userId: string, key = newKey()): Promise<Opened> {
  return send<Opened>(`/users/${userId}/password`, {}, key)
}

/** Cambia ruolo o attivazione.
 *
 * Il rifiuto `last_manager` arriva da qui e non si previene: e' il servizio che
 * sa, nel momento in cui si decide, quanti manager attivi restano. */
export function changeAccount(
  userId: string,
  change: { role?: string; active?: boolean },
): Promise<Account> {
  return patch<Account>(`/users/${userId}`, change)
}

/**
 * Che cosa fa un ruolo, detto a chi deve sceglierlo.
 *
 * **Si deduce dalla lista del servizio, non da una tabella qui.** Cinque aree
 * di lavoro, ognuna riconosciuta da un permesso che la caratterizza: se il
 * ruolo ce l'ha, quell'area e' sua; se non ce l'ha, e' di qualcun altro. Dire
 * anche il «non» conta quanto il resto: chi sceglie «operatore» deve sapere
 * che sta scegliendo qualcuno che non consegna, prima di scoprirlo quando
 * gli manca il tasto.
 */
const AREAS: { capability: string; does: MessageKey; not: MessageKey }[] = [
  { capability: 'package.upload', does: 'role.does.upload', not: 'role.not.upload' },
  { capability: 'review.decide', does: 'role.does.review', not: 'role.not.review' },
  { capability: 'quality.seal', does: 'role.does.quality', not: 'role.not.quality' },
  { capability: 'export.send', does: 'role.does.deliver', not: 'role.not.deliver' },
  { capability: 'user.manage', does: 'role.does.admin', not: 'role.not.admin' },
]

export function describeRole(may: string[]): { does: MessageKey[]; not: MessageKey[] } {
  const has = new Set(may)
  return {
    does: AREAS.filter((one) => has.has(one.capability)).map((one) => one.does),
    not: AREAS.filter((one) => !has.has(one.capability)).map((one) => one.not),
  }
}
